import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

const API_BASE = 'https://birdseye-main-326804163209.asia-southeast1.run.app';
const POLL_INTERVAL_MS = 10 * 60 * 1000;
const NOTIFIED_IDS_STORAGE_KEY = 'birdseyeNotifiedAlertIds';
const NOTIFICATION_HISTORY_STORAGE_KEY = 'birdseyeNotificationHistory';
const MAX_NOTIFICATION_HISTORY = 40;
const APP_ROUTES = ['/dashboard', '/cameras', '/history', '/profile', '/settings'];
const NOTIFICATION_FETCH_ROUTES = ['/dashboard', '/cameras', '/history'];

export const WET_THRESHOLDS = { moderate: 25, critical: 30 };
export const AMMONIA_THRESHOLDS = { moderate: 15, critical: 25 };
export const GROWTH_THRESHOLDS = { moderate: 0.5, critical: 1 };

export const getStatus = (value, thresholds) => {
  if (value < thresholds.moderate) return 'Safe';
  if (value <= thresholds.critical) return 'Moderate';
  return 'Critical';
};

const NotificationContext = createContext(null);

const getStoredAlertIds = () => {
  try {
    return JSON.parse(sessionStorage.getItem(NOTIFIED_IDS_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

const storeAlertIds = (ids) => {
  sessionStorage.setItem(NOTIFIED_IDS_STORAGE_KEY, JSON.stringify(Array.from(ids).slice(-80)));
};

const getStoredNotifications = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(NOTIFICATION_HISTORY_STORAGE_KEY) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

const storeNotifications = (notifications) => {
  localStorage.setItem(
    NOTIFICATION_HISTORY_STORAGE_KEY,
    JSON.stringify(notifications.slice(0, MAX_NOTIFICATION_HISTORY))
  );
};

const getReadingScore = (reading, index) => {
  const timestamp = reading?.iso8601_timestamp_string
    ?.replace(' ', 'T')
    .replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3');
  const parsedTime = timestamp ? new Date(timestamp).getTime() : NaN;
  if (!Number.isNaN(parsedTime)) return parsedTime;
  return Number(reading?.id) || index;
};

const getLatestReading = (readings) => (
  readings.reduce((latest, reading, index) => {
    if (!latest) return { reading, score: getReadingScore(reading, index) };
    const score = getReadingScore(reading, index);
    return score > latest.score ? { reading, score } : latest;
  }, null)?.reading ?? null
);

const parseGodJson = (data) => {
  const readings = data?.ammonia?.readings ?? [];
  const latest = getLatestReading(readings);

  return {
    wetPercentage: latest?.wet_litter_percentage ?? 0,
    ammoniaLevel: latest?.ammonia_value_mean ?? 0,
    wetnessGrowthRate: latest?.wetness_growth_rate ?? latest?.advanced?.slerExpansionRate ?? 0,
    latestTimestamp: latest?.iso8601_timestamp_string ?? null,
  };
};

const getNotificationTone = (status) => (
  status === 'Critical'
    ? {
        border: 'border-red-200',
        badge: 'bg-red-50 text-red-600',
        iconBg: 'bg-red-50 text-red-500',
      }
    : {
        border: 'border-yellow-200',
        badge: 'bg-yellow-50 text-yellow-700',
        iconBg: 'bg-yellow-50 text-yellow-600',
      }
);

const createReadingAlerts = (parsed) => {
  const metrics = [
    {
      id: 'wet-litter',
      label: 'Wet litter percentage',
      value: parsed.wetPercentage,
      unit: '%',
      precision: 1,
      thresholds: WET_THRESHOLDS,
    },
    {
      id: 'ammonia',
      label: 'Ammonia level',
      value: parsed.ammoniaLevel,
      unit: ' ppm',
      precision: 2,
      thresholds: AMMONIA_THRESHOLDS,
    },
    {
      id: 'wetness-growth',
      label: 'Wetness growth rate',
      value: parsed.wetnessGrowthRate,
      unit: '%/m',
      precision: 2,
      thresholds: GROWTH_THRESHOLDS,
    },
  ];

  return metrics
    .map((metric) => {
      const status = getStatus(metric.value, metric.thresholds);
      if (status === 'Safe') return null;
      const value = typeof metric.value === 'number'
        ? metric.value.toFixed(metric.precision)
        : metric.value;

      return {
        id: `${metric.id}-${status}-${parsed.latestTimestamp ?? 'latest'}`,
        metric: metric.label,
        status,
        value: `${value}${metric.unit}`,
        message: `${metric.label} reached ${status.toLowerCase()} level at ${value}${metric.unit}.`,
        timestamp: parsed.latestTimestamp,
        ...getNotificationTone(status),
      };
    })
    .filter(Boolean);
};

const AlertIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const BellIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const NotificationToast = ({ alert, onDismiss }) => (
  <motion.div
    layout
    initial={{ opacity: 0, x: 60, scale: 0.96 }}
    animate={{ opacity: 1, x: 0, scale: 1 }}
    exit={{ opacity: 0, x: 60, scale: 0.96 }}
    className={`w-96 bg-white border ${alert.border} rounded-2xl shadow-xl p-4 flex items-start gap-3`}
  >
    <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${alert.iconBg}`}>
      <AlertIcon />
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[11px] font-poppins font-bold px-2 py-0.5 rounded-full ${alert.badge}`}>
          {alert.status}
        </span>
        <span className="text-[11px] font-poppins text-gray-400">Just now</span>
      </div>
      <p className="font-poppins font-semibold text-sm text-gray-800 leading-snug">{alert.metric}</p>
      <p className="font-poppins text-xs text-gray-500 mt-0.5">{alert.message}</p>
    </div>
    <button
      onClick={() => onDismiss(alert.id)}
      className="text-gray-300 hover:text-gray-500 transition-colors"
      aria-label="Dismiss notification"
    >
      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  </motion.div>
);

const NotificationCenter = ({ alerts, open, onToggle, onDismiss }) => {
  const activeCount = alerts.length;

  return (
    <div className="fixed bottom-6 right-6 z-40">
      <button
        onClick={onToggle}
        className="relative w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:text-[#AC4242] transition-colors"
        aria-label="Open notifications"
      >
        <BellIcon className="w-5 h-5" />
        {activeCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#AC4242] text-white text-[10px] font-poppins font-bold flex items-center justify-center">
            {activeCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            className="absolute right-0 bottom-full mb-3 w-96 bg-white border border-black/[0.08] rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <p className="font-poppins font-bold text-sm text-gray-800">Notifications</p>
              <span className="font-poppins text-[11px] text-gray-400">{activeCount} recent</span>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {alerts.length > 0 ? alerts.map(alert => (
                <div key={alert.id} className="px-4 py-3 border-b border-gray-100 last:border-b-0 flex items-start gap-3">
                  <span className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${alert.iconBg}`}>
                    <AlertIcon />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-poppins font-bold px-2 py-0.5 rounded-full ${alert.badge}`}>
                        {alert.status}
                      </span>
                      <span className="text-[10px] font-poppins text-gray-400">{alert.value}</span>
                    </div>
                    <p className="font-poppins font-semibold text-xs text-gray-800">{alert.metric}</p>
                    <p className="font-poppins text-[11px] text-gray-500 mt-0.5 leading-snug">{alert.message}</p>
                  </div>
                  <button
                    onClick={() => onDismiss(alert.id)}
                    className="text-gray-300 hover:text-gray-500 transition-colors"
                    aria-label="Dismiss notification"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              )) : (
                <div className="px-4 py-8 text-center">
                  <p className="font-poppins font-semibold text-sm text-gray-700">No notifications yet</p>
                  <p className="font-poppins text-xs text-gray-400 mt-1">Moderate and critical readings will be saved here.</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const NotificationProvider = ({ children }) => {
  const location = useLocation();
  const [notifications, setNotifications] = useState(getStoredNotifications);
  const [visibleToasts, setVisibleToasts] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notifiedAlertIds = useRef(new Set(getStoredAlertIds()));
  const showGlobalNotifications = APP_ROUTES.includes(location.pathname) && Boolean(localStorage.getItem('authToken'));

  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(alert => alert.id !== id));
  }, []);

  const dismissToast = useCallback((id) => {
    setVisibleToasts(prev => prev.filter(alert => alert.id !== id));
  }, []);

  const queueReadingAlerts = useCallback((parsed, { forceToast = false } = {}) => {
    const newAlerts = createReadingAlerts(parsed).filter(alert => (
      forceToast || !notifiedAlertIds.current.has(alert.id)
    ));
    if (newAlerts.length === 0) return;

    newAlerts.forEach(alert => notifiedAlertIds.current.add(alert.id));
    storeAlertIds(notifiedAlertIds.current);
    setNotifications(prev => {
      const newAlertIds = new Set(newAlerts.map(alert => alert.id));
      return [
        ...newAlerts,
        ...prev.filter(alert => !newAlertIds.has(alert.id)),
      ].slice(0, MAX_NOTIFICATION_HISTORY);
    });
    const toastAlerts = forceToast
      ? newAlerts.map((alert, index) => ({
          ...alert,
          id: `${alert.id}-demo-${Date.now()}-${index}`,
        }))
      : newAlerts;

    setVisibleToasts(prev => [...toastAlerts, ...prev].slice(0, 3));

    toastAlerts.forEach(alert => {
      window.setTimeout(() => {
        setVisibleToasts(prev => prev.filter(item => item.id !== alert.id));
      }, alert.status === 'Critical' ? 9000 : 6500);
    });
  }, []);

  const fetchNotificationReadings = useCallback(async ({ forceToast = false } = {}) => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/information/getgodjson`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      queueReadingAlerts(parseGodJson(data), { forceToast });
    } catch (err) {
      console.warn('Notification fetch failed:', err.message);
    }
  }, [queueReadingAlerts]);

  useEffect(() => {
    if (!showGlobalNotifications) return undefined;
    if (!NOTIFICATION_FETCH_ROUTES.includes(location.pathname)) return undefined;

    fetchNotificationReadings({ forceToast: location.pathname === '/dashboard' });
    const id = setInterval(fetchNotificationReadings, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchNotificationReadings, location.pathname, showGlobalNotifications]);

  useEffect(() => {
    setNotificationsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    storeNotifications(notifications);
  }, [notifications]);

  const value = useMemo(() => ({
    notifications,
    dismissNotification,
    fetchNotificationReadings,
  }), [dismissNotification, fetchNotificationReadings, notifications]);

  return (
    <NotificationContext.Provider value={value}>
      {children}

      {showGlobalNotifications && (
        <>
          <div className="fixed top-6 right-6 z-50 space-y-3">
            <AnimatePresence>
              {visibleToasts.map(alert => (
                <NotificationToast
                  key={alert.id}
                  alert={alert}
                  onDismiss={dismissToast}
                />
              ))}
            </AnimatePresence>
          </div>

          <NotificationCenter
            alerts={notifications}
            open={notificationsOpen}
            onToggle={() => setNotificationsOpen(open => !open)}
            onDismiss={dismissNotification}
          />
        </>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};
